/**
 * Feedback ticket triage (task #66): list + filter tickets, and a routed
 * per-ticket page (/apps/:appId/feedback/:ticketId) so tickets are
 * shareable links. Tickets carry an assignee, status flow, and comments.
 */
import { Fragment, useEffect, useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addFeedbackComment,
  resymbolicateFeedback,
  downloadFeedbackAttachment,
  getAuthMe,
  getFeedback,
  getFeedbackBuildFilter,
  getFeedbackAttachmentBlob,
  listFeedback,
  getDeviceDetail,
  getFeedbackAttachmentText,
  updateFeedbackTicket,
} from "../lib/api";
import { feedbackMessage } from "../lib/feedbackMessages";
import { useToast } from "../components/Toast";
import { FeedbackTrends } from "../components/FeedbackTrends";
import { FeedbackBuildScopeSelector, feedbackBuildScope } from "../components/FeedbackBuildScope";
import { MarkdownText } from "../components/MarkdownText";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  EmptyStateTitle,
  Input,
  Select,
  SelectContent,
  SelectList,
  SelectIcon,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "raft-ui";
import type { BadgeProps } from "raft-ui";
import type { FeedbackTicket } from "../lib/api";

const STATUSES = ["open", "in_progress", "resolved"] as const;

type TeamClosureReason = NonNullable<FeedbackTicket["closure_reason"]>;

const TEAM_CLOSURE_REASONS: Array<{
  description: string;
  label: string;
  reason: TeamClosureReason;
}> = [
  { reason: "completed", label: "Completed", description: "The fix or improvement has been delivered." },
  { reason: "not_planned", label: "Not planned", description: "Keep the record, but do not work on it now." },
  { reason: "cannot_reproduce", label: "Cannot reproduce", description: "The issue cannot be reproduced with the available information." },
  { reason: "duplicate", label: "Duplicate", description: "Close as a duplicate and link the original ticket." },
];

function closureReasonLabel(reason: FeedbackTicket["closure_reason"]): string {
  if (reason === "no_longer_needed") return "No longer needed";
  return TEAM_CLOSURE_REASONS.find((item) => item.reason === reason)?.label
    ?? String(reason ?? "Closed");
}

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  open: "danger",
  in_progress: "information",
  resolved: "success",
  closed: "muted",
};

const KIND_VARIANTS: Record<string, BadgeVariant> = {
  feedback: "muted",
  bug: "warning",
  crash: "danger",
};

export function AppFeedback({ appId }: { appId: string }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [kindFilter, setKindFilter] = useState<string>(searchParams.get("kind") ?? "");
  const deviceFilter = searchParams.get("device_id") ?? "";
  const versionFilter = searchParams.get("version_code") ?? "";
  const signatureFilter = searchParams.get("signature") ?? "";
  const buildScope = feedbackBuildScope(searchParams.get("build_scope"));
  const originPolicy = useQuery({
    queryKey: ["feedback-build-filter", appId],
    queryFn: () => getFeedbackBuildFilter(appId),
  });

  const tickets = useQuery({
    queryKey: ["feedback", appId, statusFilter, kindFilter, deviceFilter, versionFilter, signatureFilter, buildScope],
    queryFn: () =>
      listFeedback(appId, {
        status: statusFilter || undefined,
        // Feedback tab = user feedback + bugs; crashes live in the Crashes
        // tab. An explicit kind/signature filter overrides this.
        kind: kindFilter || (signatureFilter ? "crash,error" : "feedback,bug"),
        deviceId: deviceFilter || undefined,
        versionCode: versionFilter ? Number(versionFilter) : undefined,
        signature: signatureFilter || undefined,
        buildScope,
      }),
  });

  const clearScopeFilter = (key: "device_id" | "version_code" | "signature") => {
    const next = new URLSearchParams(searchParams);
    next.delete(key);
    setSearchParams(next);
  };

  const rows = tickets.data?.tickets ?? [];

  return (
    <div className="space-y-4">
      {deviceFilter && (
        <DeviceScopeBanner
          appId={appId}
          deviceId={deviceFilter}
          onClear={() => clearScopeFilter("device_id")}
        />
      )}
      {versionFilter && (
        <div className="flex flex-row items-center justify-between rounded-md bg-fill-muted px-3 py-1.5 text-sm">
          <span className="text-foreground-muted">
            Filtered to version code <span className="font-mono text-foreground">{versionFilter}</span>
          </span>
          <Button variant="link" size="sm" className="text-xs" onClick={() => clearScopeFilter("version_code")}>
            clear
          </Button>
        </div>
      )}
      {signatureFilter && (
        <div className="rounded-md bg-fill-muted p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs text-foreground-muted">Crash signature</div>
              <code className="text-xs break-all">{signatureFilter}</code>
              {rows.length > 0 && (
                <div className="mt-1 text-xs text-foreground-muted">
                  {rows.length} instance{rows.length === 1 ? "" : "s"} ·{" "}
                  {new Set(rows.map((r) => r.version_code).filter(Boolean)).size} version(s) ·{" "}
                  {new Set(rows.map((r) => r.device_id).filter(Boolean)).size} device(s)
                </div>
              )}
            </div>
            <Button variant="link" size="sm" className="text-xs flex-none" onClick={() => clearScopeFilter("signature")}>
              clear
            </Button>
          </div>
        </div>
      )}
      {!deviceFilter && !versionFilter && !signatureFilter && <FeedbackTrends appId={appId} buildScope={buildScope} />}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold">Feedback</h2>
          <p className="text-sm text-foreground-muted">
            Feedback and crash reports submitted from this app.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Select
            items={{ "": "All statuses", ...Object.fromEntries(STATUSES.map((s) => [s, s])) }}
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as string)}
          >
            <SelectTrigger className="w-auto! py-1.5!">
              <SelectValue />
              <SelectIcon />
            </SelectTrigger>
            <SelectContent>
              <SelectList>
              <SelectItem value="">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectList>
            </SelectContent>
          </Select>
          <Select
            items={{ "": "All kinds", feedback: "feedback", bug: "bug", crash: "crash", error: "error" }}
            value={kindFilter}
            onValueChange={(v) => setKindFilter(v as string)}
          >
            <SelectTrigger className="w-auto! py-1.5!">
              <SelectValue />
              <SelectIcon />
            </SelectTrigger>
            <SelectContent>
              <SelectList>
              <SelectItem value="">All kinds</SelectItem>
              <SelectItem value="feedback">feedback</SelectItem>
              <SelectItem value="bug">bug</SelectItem>
              <SelectItem value="crash">crash</SelectItem>
            </SelectList>
            </SelectContent>
          </Select>
        </div>
      </div>


      {originPolicy.data?.default_enabled === 1 && (
        <FeedbackBuildScopeSelector value={buildScope} onChange={(value) => {
          const next = new URLSearchParams(searchParams);
          if (value === "standard") next.delete("build_scope"); else next.set("build_scope", value);
          setSearchParams(next, { replace: true });
        }} />
      )}
      <div className="overflow-x-auto">
        {tickets.isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}
        {tickets.error && (
          <p className="text-sm text-danger">
            Failed to load feedback: {(tickets.error as Error).message}
          </p>
        )}
        {!tickets.isLoading && rows.length === 0 && (
          <EmptyState>
            <EmptyStateTitle>No feedback tickets yet.</EmptyStateTitle>
          </EmptyState>
        )}
        {rows.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-foreground-muted border-b border-line-muted">
                <th className="py-2 pr-3">Ticket</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Assignee</th>
                <th className="py-2 pr-3">App version</th>
                <th className="py-2 pr-3">Device</th>
                <th className="py-2 pr-3">Created</th>
                <th className="py-2 pr-3">📎</th>
                <th className="py-2 pr-3">💬</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr
                  key={t.id}
                  className="border-b border-line-hairline last:border-0 cursor-pointer hover:bg-fill-muted"
                  onClick={() => navigate(`/apps/${appId}/feedback/${t.id}`)}
                >
                  <td className="py-2 pr-3 max-w-md">
                    <Badge variant={KIND_VARIANTS[t.kind] ?? "muted"} className="mr-2">
                      {t.kind}
                    </Badge>
                    {t.crash_type === "anr" && (
                      <Badge variant="warning" className="mr-2">
                        ANR
                      </Badge>
                    )}
                    {t.build_origin === "external" && <Badge variant="warning" className="mr-2">External</Badge>}
                    {t.build_origin === "unknown" && <Badge variant="muted" className="mr-2">Origin unknown</Badge>}
                    <span className="align-middle">{t.message.slice(0, 80)}{t.message.length > 80 ? "…" : ""}</span>
                  </td>
                  <td className="py-2 pr-3">
                    <Badge variant={STATUS_VARIANTS[t.status] ?? "muted"}>
                      {t.status}
                    </Badge>
                  </td>
                  <td className="py-2 pr-3 text-xs text-foreground">{t.assignee ?? "—"}</td>
                  <td className="py-2 pr-3 text-xs text-foreground">
                    {t.version_name ?? "—"}{t.version_code ? ` (${t.version_code})` : ""}
                  </td>
                  <td className="py-2 pr-3 text-xs text-foreground">
                    {[t.device_model, t.os_version].filter(Boolean).join(" · ") || "—"}
                  </td>
                  <td className="py-2 pr-3 text-xs text-foreground">
                    {new Date(t.created_at).toLocaleString()}
                  </td>
                  <td className="py-2 pr-3 text-xs">{t.attachment_count || ""}</td>
                  <td className="py-2 pr-3 text-xs">{t.comment_count || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

type CrashSection = { key: string; label: string; body: string };

/**
 * Split a Hands crash log into sections by its known headers, so the ticket
 * UI can present them as tabs (like Bugly's stack / scene / logs panels).
 */
function parseCrashLog(text: string): CrashSection[] {
  const headers: Array<{ marker: string; key: string; label: string }> = [
    { marker: "Stack trace:", key: "stack", label: "Stack" },
    { marker: "All threads:", key: "threads", label: "All threads" },
    { marker: "Process info:", key: "process", label: "Process" },
    { marker: "Open file descriptors:", key: "fds", label: "File descriptors" },
    { marker: "Recent logcat:", key: "logs", label: "Logs" },
    { marker: "App context:", key: "context", label: "App context" },
  ];
  const lines = text.split("\n");
  // Everything before the first known header is the summary/device header.
  const marks: Array<{ i: number; key: string; label: string }> = [];
  lines.forEach((line, i) => {
    const h = headers.find((x) => line.trim() === x.marker);
    if (h) marks.push({ i, key: h.key, label: h.label });
  });
  const sections: CrashSection[] = [];
  const head = lines.slice(0, marks.length ? marks[0]!.i : lines.length).join("\n").trim();
  if (head) sections.push({ key: "summary", label: "Summary", body: head });
  marks.forEach((m, idx) => {
    const end = idx + 1 < marks.length ? marks[idx + 1]!.i : lines.length;
    const body = lines.slice(m.i + 1, end).join("\n").trimEnd();
    if (body.trim()) sections.push({ key: m.key, label: m.label, body });
  });
  return sections;
}

function CrashLogView({
  appId,
  ticketId,
  attachmentId,
  deobfuscated,
}: {
  appId: string;
  ticketId: string;
  attachmentId: string;
  deobfuscated?: string | undefined;
}) {
  const log = useQuery({
    queryKey: ["crash-log", appId, ticketId, attachmentId],
    queryFn: () => getFeedbackAttachmentText(appId, ticketId, attachmentId),
  });
  const [active, setActive] = useState<string>("stack");
  const [showDeobf, setShowDeobf] = useState(true);

  const sections = useMemo(() => (log.data ? parseCrashLog(log.data) : []), [log.data]);
  const activeSection =
    sections.find((x) => x.key === active) ?? sections[0];

  return (
    <section className="border-t border-line-hairline pt-5">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-medium text-foreground-strong">Crash detail</h4>
        {deobfuscated && (active === "stack" || (activeSection?.key === "stack")) && (
          <div className="flex overflow-hidden rounded-md bg-fill-muted text-xs">
            <Button
              size="sm"
              variant={showDeobf ? "default" : "ghost"}
              onClick={() => setShowDeobf(true)}
            >
              Deobfuscated
            </Button>
            <Button
              size="sm"
              variant={!showDeobf ? "default" : "ghost"}
              onClick={() => setShowDeobf(false)}
            >
              Raw
            </Button>
          </div>
        )}
      </div>

      {log.isLoading && <p className="text-xs text-foreground-muted">Loading…</p>}
      {log.error && <p className="text-xs text-danger">Could not load crash log.</p>}

      {sections.length > 0 && (
        <>
          <div className="mb-2 flex flex-wrap gap-1 border-b border-line-hairline pb-2 text-xs">
            {sections.map((sec) => (
              <Button
                key={sec.key}
                size="sm"
                variant={(activeSection?.key ?? "") === sec.key ? "default" : "ghost"}
                onClick={() => setActive(sec.key)}
              >
                {sec.label}
              </Button>
            ))}
          </div>
          <pre className="max-h-112 overflow-auto rounded-md bg-layer-hud p-3 text-xs leading-relaxed text-layer-hud-foreground">
            {activeSection?.key === "stack" && deobfuscated && showDeobf
              ? deobfuscated
              : activeSection?.body}
          </pre>
        </>
      )}
    </section>
  );
}

function DeviceScopeBanner({
  appId,
  deviceId,
  onClear,
}: {
  appId: string;
  deviceId: string;
  onClear: () => void;
}) {
  const detail = useQuery({
    queryKey: ["device-detail", appId, deviceId],
    queryFn: () => getDeviceDetail(appId, deviceId),
  });
  const d = detail.data?.device;
  return (
    <div className="rounded-md bg-fill-muted p-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">
          Device <span className="font-mono text-xs">{deviceId}</span>
        </h4>
        <Button variant="link" size="sm" className="text-xs" onClick={onClear}>
          clear
        </Button>
      </div>
      {d ? (
        <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
          <div>
            <dt className="text-foreground-muted">Latest version</dt>
            <dd>{d.version_name ?? "—"}{d.version_code ? ` (${d.version_code})` : ""}</dd>
          </div>
          <div>
            <dt className="text-foreground-muted">Model</dt>
            <dd>{d.device_model ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-foreground-muted">Platform / OS</dt>
            <dd>{[d.platform, d.os_version].filter(Boolean).join(" · ") || "—"}</dd>
          </div>
          <div>
            <dt className="text-foreground-muted">Last seen</dt>
            <dd>{new Date(d.last_seen).toLocaleString()}</dd>
          </div>
        </dl>
      ) : (
        <p className="mt-1 text-xs text-foreground-muted">
          No analytics ping from this device yet — showing its tickets below.
        </p>
      )}
    </div>
  );
}

const IMAGE_EXTS = /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i;

function isImageAttachment(a: { content_type: string | null; filename: string }): boolean {
  return (a.content_type?.startsWith("image/") ?? false) || IMAGE_EXTS.test(a.filename);
}

function AttachmentList({
  appId,
  ticketId,
  attachments,
}: {
  appId: string;
  ticketId: string;
  attachments: Array<{ id: string; filename: string; content_type: string | null; size_bytes: number }>;
}) {
  const [lightbox, setLightbox] = useState<string | null>(null);
  const images = attachments.filter(isImageAttachment);
  const others = attachments.filter((a) => !isImageAttachment(a));

  return (
    <section className="border-t border-line-hairline pt-5">
      <h4 className="mb-3 text-sm font-medium text-foreground-strong">Attachments</h4>

      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {images.map((a) => {
            return (
              <AttachmentImage
                key={a.id}
                appId={appId}
                ticketId={ticketId}
                attachment={a}
                onOpen={setLightbox}
              />
            );
          })}
        </div>
      )}

      {others.length > 0 && (
        <ul className="space-y-1 text-sm">
          {others.map((a) => (
            <li key={a.id}>
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => void downloadFeedbackAttachment(appId, ticketId, a.id, a.filename)}
              >
                {a.filename}
              </Button>
              <span className="ml-2 text-xs text-foreground-hint">
                {(a.size_bytes / 1024).toFixed(1)} KB
              </span>
            </li>
          ))}
        </ul>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
          onClick={() => setLightbox(null)}
        >
          <img
            src={lightbox}
            alt=""
            className="max-h-full max-w-full rounded-md shadow-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <Button
            variant="ghost"
            size="icon-sm"
            className="absolute right-5 top-5 text-2xl leading-none text-white/90 hover:text-white"
            onClick={() => setLightbox(null)}
            aria-label="Close"
          >
            ×
          </Button>
        </div>
      )}
    </section>
  );
}

function AttachmentImage({
  appId,
  ticketId,
  attachment,
  onOpen,
}: {
  appId: string;
  ticketId: string;
  attachment: { id: string; filename: string };
  onOpen: (url: string) => void;
}) {
  const image = useQuery({
    queryKey: ["feedback-attachment-image", appId, ticketId, attachment.id],
    queryFn: async () => URL.createObjectURL(
      await getFeedbackAttachmentBlob(appId, ticketId, attachment.id, true),
    ),
    staleTime: Infinity,
  });

  useEffect(() => {
    const url = image.data;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [image.data]);

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            className="group relative h-24 w-24 overflow-hidden rounded-md bg-layer-canvas-muted"
            onClick={() => image.data && onOpen(image.data)}
            disabled={!image.data}
          >
            {image.data && (
              <img
                src={image.data}
                alt={attachment.filename}
                loading="lazy"
                className="h-full w-full object-cover transition group-hover:opacity-90"
              />
            )}
          </button>
        }
      />
      <TooltipContent>{attachment.filename}</TooltipContent>
    </Tooltip>
  );
}

export function FeedbackTicketPage({
  appId,
  ticketId,
}: {
  appId: string;
  ticketId: string;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState("");
  const [assigneeDraft, setAssigneeDraft] = useState<string | null>(null);
  const [pendingClosureReason, setPendingClosureReason] =
    useState<TeamClosureReason | null>(null);
  const [duplicateOfTicketId, setDuplicateOfTicketId] = useState("");

  const detail = useQuery({
    queryKey: ["feedback-detail", appId, ticketId],
    queryFn: () => getFeedback(appId, ticketId),
  });
  const me = useQuery({ queryKey: ["auth-me"], queryFn: getAuthMe });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["feedback-detail", appId, ticketId] });
    queryClient.invalidateQueries({ queryKey: ["feedback", appId] });
  };

  const update = useMutation({
    mutationFn: (body: {
      status?: string;
      assignee?: string | null;
      closure_reason?: FeedbackTicket["closure_reason"];
      duplicate_of_ticket_id?: string | null;
    }) =>
      updateFeedbackTicket(appId, ticketId, body),
    onSuccess: () => {
      setAssigneeDraft(null);
      invalidate();
    },
    onError: (e) =>
      toast.show({ kind: "error", title: "Update failed", description: (e as Error).message }),
  });

  const addComment = useMutation({
    mutationFn: (internal: boolean) =>
      addFeedbackComment(appId, ticketId, comment.trim(), internal),
    onSuccess: () => {
      setComment("");
      invalidate();
    },
    onError: (e) =>
      toast.show({ kind: "error", title: "Comment failed", description: (e as Error).message }),
  });

  const resymbolicate = useMutation({
    mutationFn: () => resymbolicateFeedback(appId, ticketId),
    onSuccess: (r) => {
      invalidate();
      toast.show({
        kind: r.symbolication_status === "symbolicated" ? "success" : "info",
        title: `Symbolication: ${r.symbolication_status ?? "done"}`,
      });
    },
    onError: (e) =>
      toast.show({ kind: "error", title: "Symbolicate failed", description: (e as Error).message }),
  });

  const t = detail.data?.ticket;
  const myName = me.data?.account?.display_name ?? null;
  const pendingClosure = TEAM_CLOSURE_REASONS.find(
    (item) => item.reason === pendingClosureReason,
  );
  const submitClosure = () => {
    if (!pendingClosureReason) return;
    update.mutate(
      {
        status: "closed",
        closure_reason: pendingClosureReason,
        duplicate_of_ticket_id:
          pendingClosureReason === "duplicate" ? duplicateOfTicketId.trim() : null,
      },
      {
        onSuccess: () => {
          setPendingClosureReason(null);
          setDuplicateOfTicketId("");
        },
      },
    );
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-2 text-sm">
        <Link to={`/apps/${appId}/feedback`} className="text-info-strong hover:underline">
          ← Feedback
        </Link>
      </div>

      {detail.isLoading && <p className="text-sm text-foreground-muted">Loading…</p>}
      {detail.error && (
        <p className="text-sm text-danger">{(detail.error as Error).message}</p>
      )}
      {t && (
        <>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <Badge variant={KIND_VARIANTS[t.kind] ?? "muted"}>{t.kind}</Badge>
                <Badge variant={STATUS_VARIANTS[t.status] ?? "muted"}>{t.status}</Badge>
                {t.assignee && (
                  <span className="rounded-sm bg-accent-soft px-1.5 py-0.5 text-xs font-medium text-accent-strong">
                    {t.assignee}
                  </span>
                )}
              </div>
              <h2 className="mt-2 text-lg font-semibold">Ticket {t.id.slice(0, 8)}</h2>
              <p className="text-xs text-foreground-muted">
                {new Date(t.created_at).toLocaleString()}
                {t.contact ? ` · ${t.contact}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {STATUSES.map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={s === t.status ? "default" : "ghost"}
                  disabled={update.isPending || s === t.status}
                  onClick={() => update.mutate({ status: s })}
                >
                  {s}
                </Button>
              ))}
              {t.status !== "closed" && (
                <div className="inline-flex">
                  <Button
                    size="sm"
                    variant="primary"
                    className="rounded-r-none"
                    disabled={update.isPending}
                    onClick={() => setPendingClosureReason("completed")}
                  >
                    Completed
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      render={
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          className="min-w-11 rounded-l-none border-l-0 px-3"
                          disabled={update.isPending}
                          aria-label="Choose a close reason"
                        />
                      }
                    >
                      <ChevronDown aria-hidden="true" className="size-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-80 max-w-[calc(100vw-24px)]">
                      {TEAM_CLOSURE_REASONS.map((item) => (
                        <DropdownMenuItem
                          key={item.reason}
                          className="items-start whitespace-normal"
                          onClick={() => setPendingClosureReason(item.reason)}
                        >
                          {item.reason === "completed" ? (
                            <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                          ) : (
                            <span aria-hidden="true" className="size-4 shrink-0" />
                          )}
                          <span>
                            <strong className="block">{item.label}</strong>
                            <span className="mt-0.5 block text-xs text-foreground-muted">
                              {item.description}
                            </span>
                          </span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          </div>

          {t.status === "closed" && t.closure_reason && (
            <div className="rounded-md bg-fill-muted p-3 text-sm">
              <div className="font-medium text-foreground-strong">Closed as {closureReasonLabel(t.closure_reason)}</div>
              {t.duplicate_of_ticket_id && (
                <Link
                  to={`/apps/${appId}/feedback/${t.duplicate_of_ticket_id}`}
                  className="mt-1 inline-block font-mono text-xs text-info-strong hover:underline"
                >
                  Original ticket {t.duplicate_of_ticket_id}
                </Link>
              )}
            </div>
          )}

          <MarkdownText source={t.message} className="text-sm leading-relaxed" />

          <section className="border-t border-line-hairline pt-5">
            <h4 className="mb-2 text-sm font-medium text-foreground-strong">Assignee</h4>
            <div className="flex items-center gap-2 text-sm flex-wrap">
              {assigneeDraft === null ? (
                <>
                  <span>{t.assignee ?? "Unassigned"}</span>
                  {myName && t.assignee !== myName && (
                    <Button
                      variant="link"
                      size="sm"
                      className="text-xs"
                      disabled={update.isPending}
                      onClick={() => update.mutate({ assignee: myName })}
                    >
                      Assign to me
                    </Button>
                  )}
                  <Button
                    variant="link"
                    size="sm"
                    className="text-xs"
                    onClick={() => setAssigneeDraft(t.assignee ?? "")}
                  >
                    Edit
                  </Button>
                  {t.assignee && (
                    <Button
                      variant="link"
                      size="sm"
                      className="text-xs text-danger"
                      disabled={update.isPending}
                      onClick={() => update.mutate({ assignee: null })}
                    >
                      Unassign
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Input
                    className="max-w-64 text-sm"
                    value={assigneeDraft}
                    autoFocus
                    onChange={(e) => setAssigneeDraft(e.target.value)}
                    placeholder="Name of the person handling this"
                  />
                  <Button
                    variant="primary"
                    className="text-xs"
                    disabled={update.isPending}
                    onClick={() => update.mutate({ assignee: assigneeDraft.trim() || null })}
                  >
                    Save
                  </Button>
                  <Button variant="outline" className="text-xs" onClick={() => setAssigneeDraft(null)}>
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </section>

          <section className="border-t border-line-hairline pt-5">
            <h4 className="mb-2 text-sm font-medium text-foreground-strong">Environment</h4>
            {(() => {
              // Generic render of every reported environment field (task #105):
              // no hardcoded property allowlist — parse the ticket metadata and
              // list each field, so new SDK-reported fields show up with zero
              // UI changes. crash_* keys feed the symbolication panel below and
              // are excluded here. version_code / device_id keep their filter
              // links; commit is shown monospace.
              let meta: Record<string, unknown> = {};
              try {
                const parsed = JSON.parse(t.metadata_json || "{}");
                if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                  meta = parsed as Record<string, unknown>;
                }
              } catch {
                /* malformed metadata — fall through to empty */
              }
              const entries = Object.entries(meta)
                .filter(
                  ([k, v]) =>
                    !k.startsWith("crash_") &&
                    k !== "breadcrumbs" &&
                    k !== "stacktrace" &&
                    k !== "exception_class" &&
                    k !== "exception_message" &&
                    k !== "top_frame" &&
                    k !== "handled" &&
                    v !== null &&
                    v !== undefined &&
                    v !== "",
                )
                .sort(([a], [b]) => a.localeCompare(b));
              if (entries.length === 0) {
                return (
                  <p className="text-xs text-foreground-muted">
                    No environment data reported.
                  </p>
                );
              }
              const label = (k: string) =>
                k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
              const format = (v: unknown) =>
                typeof v === "object" ? JSON.stringify(v) : String(v);
              const mono = (k: string) =>
                k === "device_id" || k === "commit" || k === "git_commit";
              return (
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {entries.map(([k, v]) => (
                    <Fragment key={k}>
                      <dt className="text-foreground-muted">{label(k)}</dt>
                      <dd className={mono(k) ? "font-mono break-all" : "break-all"}>
                        {k === "version_code" ? (
                          <Tooltip>
                            <TooltipTrigger
                              render={
                                <Button
                                  variant="link"
                                  size="sm"
                                  onClick={() =>
                                    navigate(
                                      `/apps/${appId}/feedback?version_code=${encodeURIComponent(format(v))}`,
                                    )
                                  }
                                >
                                  {format(v)}
                                </Button>
                              }
                            />
                            <TooltipContent>All feedback on this version</TooltipContent>
                          </Tooltip>
                        ) : k === "device_id" ? (
                          <Tooltip>
                            <TooltipTrigger
                              render={
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="font-mono break-all"
                                  onClick={() =>
                                    navigate(
                                      `/apps/${appId}/feedback?device_id=${encodeURIComponent(format(v))}`,
                                    )
                                  }
                                >
                                  {format(v)}
                                </Button>
                              }
                            />
                            <TooltipContent>This device's history</TooltipContent>
                          </Tooltip>
                        ) : (
                          format(v)
                        )}
                      </dd>
                    </Fragment>
                  ))}
                </dl>
              );
            })()}
          </section>

          {(() => {
            let meta: Record<string, unknown> = {};
            try {
              const parsed = JSON.parse(t.metadata_json || "{}");
              if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) meta = parsed as Record<string, unknown>;
            } catch { /* ignore */ }
            const stacktrace = typeof meta.stacktrace === "string" ? meta.stacktrace : null;
            const excClass = typeof meta.exception_class === "string" ? meta.exception_class : null;
            const excMsg = typeof meta.exception_message === "string" ? meta.exception_message : null;
            if (!stacktrace && !excClass) return null;
            return (
              <section className="border-t border-line-hairline pt-5">
                <h4 className="mb-2 text-sm font-medium text-foreground-strong">Exception</h4>
                {excClass && (
                  <p className="text-sm font-mono text-danger mb-2 break-all">
                    {excClass}{excMsg ? `: ${excMsg}` : ""}
                  </p>
                )}
                {stacktrace && (
                  <pre className="text-xs bg-layer-canvas-muted rounded p-3 overflow-x-auto max-h-80 overflow-y-auto whitespace-pre-wrap break-all">
                    {stacktrace}
                  </pre>
                )}
              </section>
            );
          })()}

          {(() => {
            let meta: Record<string, unknown> = {};
            try {
              const parsed = JSON.parse(t.metadata_json || "{}");
              if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) meta = parsed as Record<string, unknown>;
            } catch { /* ignore */ }
            let crumbs: Array<{ timestamp?: number; category?: string; message?: string; level?: string }> = [];
            if (Array.isArray(meta.breadcrumbs)) crumbs = meta.breadcrumbs as typeof crumbs;
            else if (typeof meta.breadcrumbs === "string") {
              try { const p = JSON.parse(meta.breadcrumbs); if (Array.isArray(p)) crumbs = p; } catch { /* ignore */ }
            }
            if (crumbs.length === 0) return null;
            const levelColor = (l?: string) =>
              l === "error" ? "text-danger" : l === "warning" ? "text-warning" : "text-foreground";
            return (
              <section className="border-t border-line-hairline pt-5">
                <h4 className="mb-2 text-sm font-medium text-foreground-strong">Breadcrumbs ({crumbs.length})</h4>
                <ol className="space-y-1 text-xs max-h-60 overflow-y-auto">
                  {crumbs.map((bc, i) => (
                    <li key={i} className="flex gap-2 items-baseline">
                      <span className="text-foreground-hint tabular-nums shrink-0">
                        {bc.timestamp ? new Date(bc.timestamp).toLocaleTimeString() : "—"}
                      </span>
                      <span className="font-medium text-foreground-strong shrink-0">{bc.category ?? "general"}</span>
                      <span className={`break-all ${levelColor(bc.level)}`}>{bc.message ?? ""}</span>
                    </li>
                  ))}
                </ol>
              </section>
            );
          })()}

          {(t.kind === "crash" || t.kind === "error") &&
            (() => {
              const log = detail.data!.attachments.find(
                (a) => (a.content_type?.startsWith("text/") ?? false) || /\.txt$/i.test(a.filename),
              );
              const symStatus = t.symbolication_status;
              const symStack = t.symbolicated_stack ?? undefined;
              const badgeVariant: BadgeVariant =
                symStatus === "symbolicated"
                  ? "success"
                  : symStatus === "no_symbols"
                    ? "warning"
                    : symStatus === "failed"
                      ? "danger"
                      : "muted";
              return (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium">Symbolication</span>
                    <Badge variant={badgeVariant}>
                      {symStatus ?? "not run"}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={resymbolicate.isPending}
                      onClick={() => resymbolicate.mutate()}
                    >
                      {resymbolicate.isPending ? "Symbolicating…" : "Re-run"}
                    </Button>
                  </div>
                  {log ? (
                    <CrashLogView
                      appId={appId}
                      ticketId={ticketId}
                      attachmentId={log.id}
                      deobfuscated={symStack}
                    />
                  ) : symStack ? (
                    <pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-layer-canvas-muted p-3 text-xs">
                      {symStack}
                    </pre>
                  ) : null}
                </div>
              );
            })()}

          {detail.data!.attachments.length > 0 && (
            <AttachmentList
              appId={appId}
              ticketId={ticketId}
              attachments={detail.data!.attachments}
            />
          )}

          <section className="border-t border-line-hairline pt-5">
            <h4 className="mb-3 text-sm font-medium text-foreground-strong">
              Comments ({detail.data!.comments.length})
            </h4>
            <ul className="space-y-4 text-sm">
              {detail.data!.comments.map((cm, index) => (
                <li
                  key={cm.id}
                  className={index === 0 ? "" : "border-t border-line-hairline pt-4"}
                >
                  <div className="flex items-center justify-between gap-2 text-xs text-foreground-muted">
                    <span>
                      <span className="font-medium text-foreground-strong">{cm.author_actor}</span> ·{" "}
                      {new Date(cm.created_at).toLocaleString()}
                    </span>
                    {cm.internal ? (
                      <Badge variant="warning">{feedbackMessage("internalNote")}</Badge>
                    ) : (
                      <Badge variant="muted">{feedbackMessage("visibleToReporter")}</Badge>
                    )}
                  </div>
                  <MarkdownText source={cm.body} className="mt-1.5 leading-relaxed" />
                </li>
              ))}
            </ul>
            <div className="mt-4">
              <textarea
                rows={3}
                className="w-full resize-y rounded-md border border-line-muted bg-layer-panel px-2 py-1.5 text-sm"
                placeholder="Add a comment…"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && comment.trim()) {
                    addComment.mutate(true);
                  }
                }}
              />
              <div className="mt-2 flex justify-end gap-2">
                <Button
                  className="text-sm"
                  disabled={addComment.isPending || !comment.trim()}
                  onClick={() => addComment.mutate(true)}
                >
                  {feedbackMessage("addInternalNote")}
                </Button>
                <Button
                  variant="primary"
                  className="text-sm"
                  disabled={addComment.isPending || !comment.trim()}
                  onClick={() => addComment.mutate(false)}
                >
                  {feedbackMessage("sendToReporter")}
                </Button>
              </div>
            </div>
          </section>
        </>
      )}
      <AlertDialog
        open={pendingClosureReason !== null}
        onOpenChange={(open) => {
          if (!open && !update.isPending) {
            setPendingClosureReason(null);
            setDuplicateOfTicketId("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close this ticket?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody className="space-y-3">
            <AlertDialogDescription>
              {pendingClosure?.description}
            </AlertDialogDescription>
            {pendingClosureReason === "duplicate" && (
              <label className="block text-sm font-medium">
                Original ticket ID
                <Input
                  className="mt-1"
                  value={duplicateOfTicketId}
                  onChange={(event) => setDuplicateOfTicketId(event.target.value)}
                  placeholder="Full ID or a unique prefix"
                  autoFocus
                />
              </label>
            )}
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={update.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="primary"
              loading={update.isPending}
              disabled={
                update.isPending
                || (pendingClosureReason === "duplicate" && !duplicateOfTicketId.trim())
              }
              onClick={submitClosure}
            >
              Close ticket
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
